import { Alert, Button, Checkbox, Input, Modal } from "@arco-design/web-react";
import { GitCompare, History as HistoryIcon, ReceiptText } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { normalizeHttpError, resolveReadableHttpErrorMessage } from "../../../lib/http/errors";
import { useBusinessPageTitle } from "../../../lib/navigation/useBusinessPageTitle";
import { findExistingCustomerQuote } from "../repo/quoteBudgetRepo";
import { useUnsavedChangesGuard } from "../../../shared/navigation/useUnsavedChangesGuard";
import { useQuoteBudgetEditorRuntime } from "../runtime/useQuoteBudgetEditorRuntime";
import { isBusinessTaxLine } from "../service/quoteBudgetCalculationService";
import {
  CONFIRMABLE_FINALIZE_RISK_CODES,
  finalizeBlockText,
  formatZeroKeyLineRiskSummary,
  formatCustomerQuoteLockedAt,
  isConfirmableFinalizeRisk,
  isCustomerQuoteLockError,
  isRosterPricingLockError,
  isItineraryChangedFinalizeError,
  isMissingPointUnitPrice,
  readonlyReasonText,
  selectLocateRiskLine,
  versionStatusText,
} from "../service/quoteBudgetEditorViewService";
import { useBusVehicleTypeCandidates } from "../../bus-vehicle-type";
import { getPendingWorkspaceEntries, hasPendingWorkspaceFormula, pruneStaleReplaceDrawers, remapSavedWorkspace, workspaceCategory } from "../service/quoteBudgetWorkspaceService";
import { QuoteBudgetManualWorkspace } from "./QuoteBudgetManualWorkspace";
import { useQuoteBudgetLocalDraft } from "../runtime/useQuoteBudgetLocalDraft";
import { quoteBudgetPeopleInputError } from "../service/quoteBudgetPeopleService";
import type { QuoteBudgetLocalPayload, QuoteBudgetWorkspaceState } from "../types/localDraft";
import { QuoteHistoryDrawer } from "./QuoteHistoryDrawer";
import { QuotePeopleCountPanel } from "./QuotePeopleCountPanel";
import { QuoteRebuildFromItineraryDialog } from "./QuoteRebuildFromItineraryDialog";
import { QuoteTripCard } from "./QuoteTripCard";
import { QuoteVersionActions } from "./QuoteVersionActions";
import { QuoteBudgetImportButton } from "./QuoteBudgetImportButton";
import { QuoteVersionPanel } from "./QuoteVersionPanel";
import "./quoteBudgetEditor.css";
import "./quoteBudgetManualWorkspace.css";

/** 本地时间串，仅用于保存完成提示，不入库。 */
function nowLabel(): string {
  return new Date().toLocaleTimeString("zh-CN", { hour12: false });
}

/** 编辑器首屏失败使用稳定中文，不把 Axios 等底层异常直接暴露给用户。 */
function editorLoadErrorText(error: unknown): string {
  const normalized = normalizeHttpError(error);
  if (normalized.statusCode === 408 || normalized.statusCode === 504 || normalized.code === "ECONNABORTED") {
    return "核价加载超时，请检查网络后重试。";
  }
  if (normalized.code === "ERR_NETWORK" || normalized.code === "NETWORK_ERROR") {
    return "无法连接核价服务，请检查网络连接后重试。";
  }
  if (normalized.code === "QUOTE_BUDGET_SCHEMA_NOT_READY") {
    return "数据库结构尚未完成升级，请联系管理员完成受控升级后重新加载。";
  }
  if (normalized.statusCode === 403) return "当前账号没有查看此核价的权限，请联系管理员。";
  if (normalized.statusCode === 404) return "此核价不存在或已被删除，请返回行程列表重新选择。";
  if (normalized.statusCode === 401) return "登录状态已失效，请重新登录后打开此核价。";
  return "行程核价加载失败，请稍后重试。";
}

/** 首屏加载骨架：保持编辑器两栏结构，避免首次进入时页面跳动. */
function QuoteBudgetLoadingSkeleton() {
  return (
    <div className="quote-budget-editor quote-budget-editor--loading" role="status" aria-label="行程核价加载中">
      <header className="quote-budget-editor__header">
        <span className="quote-budget-skeleton quote-budget-skeleton--eyebrow" />
        <span className="quote-budget-skeleton quote-budget-skeleton--title" />
        <span className="quote-budget-skeleton quote-budget-skeleton--meta" />
      </header>
      <div className="quote-budget-editor__workspace">
        <aside
          className="quote-budget-editor__rail"
          aria-label="核价左侧信息栏"
          data-scroll-container="left"
        >
          <div className="quote-budget-trip-card quote-budget-skeleton-card" />
          <div className="quote-budget-people quote-budget-skeleton-card" />
          <div className="quote-budget-version-panel quote-budget-skeleton-card" />
        </aside>
        <section
          className="quote-budget-editor__center"
          aria-label="核价右侧编辑区"
          data-scroll-container="right"
        >
          <div className="quote-budget-editor__scenes quote-budget-skeleton-card" />
          <div className="quote-budget-editor__entry quote-budget-skeleton-card" />
          <div className="quote-budget-detail quote-budget-skeleton-card" />
        </section>
      </div>
    </div>
  );
}

/** 按自然日且包含首尾计算行程天数，日期不完整或倒序时不生成动态示例。 */
function inclusiveItineraryDayCount(startDate?: string | null, endDate?: string | null): number | undefined {
  const parse = (value?: string | null) => {
    const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return match
      ? Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
      : Number.NaN;
  };
  const start = parse(startDate);
  const end = parse(endDate);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return undefined;
  return Math.floor((end - start) / 86_400_000) + 1;
}

/** 编辑器特性页属性. */
export type QuoteBudgetEditorFeaturePageProps = {
  budgetOrderId: string;
  returnTo?: string;
  userId?: string;
  permissionsReady?: boolean;
  canOperate?: boolean;
  canCreateCustomerQuote?: boolean;
  canViewCustomerQuote?: boolean;
  canViewItinerary?: boolean;
  canEditItinerary?: boolean;
  canImportConfirmation?: boolean;
};

/** 核价预算编辑器特性页：人数编辑、自然语言录入、汇总预览、保存草稿，只读时禁用保存. */
export function QuoteBudgetEditorFeaturePage({
  budgetOrderId,
  returnTo,
  userId,
  permissionsReady = true,
  canOperate = true,
  canCreateCustomerQuote = true,
  canViewCustomerQuote = canCreateCustomerQuote,
  canViewItinerary = false,
  canEditItinerary = false,
  canImportConfirmation = false,
}: QuoteBudgetEditorFeaturePageProps) {
  const runtime = useQuoteBudgetEditorRuntime(budgetOrderId, canOperate);
  const navigate = useNavigate();
  const canCreateCustomerQuoteFromBudget = canCreateCustomerQuote;
  // 未确认抽屉与公式输入独立于已加入费用，页面切换由离开保护统一处理。
  const [workspace, setWorkspace] = useState<QuoteBudgetWorkspaceState>({ activeCategory: "stay", activeDrawerKey: null, drawers: {}, formula: null });
  const needsLegacyBusTemplate = Object.values(workspace.drawers).some(draft => draft.category === "bus"
    && !draft.replaceIds.length && draft.initialText === undefined && !draft.textEdited && draft.text.trim().length > 0);
  const legacyBusVehicles = useBusVehicleTypeCandidates(needsLegacyBusTemplate && canOperate && !runtime.readonly);
  const pendingEntries = getPendingWorkspaceEntries(workspace, {
    lines: runtime.lines, people: runtime.draft ?? {},
    entryOptions: runtime.editor?.entryOptions ?? { stayAreas: [], points: [] },
    dayCount: inclusiveItineraryDayCount(runtime.editor?.itinerarySummary?.startDate, runtime.editor?.itinerarySummary?.endDate),
    vehicleTypes: legacyBusVehicles.data,
  });
  const hasPendingEntryText = pendingEntries.length > 0;
  const pendingEntryLabels = [...new Set(pendingEntries.map(entry => entry.label))].join("、");
  const pendingEntryFinalizeText = `未确认录入：${pendingEntryLabels}。请确认加入/修改，或清空输入/取消修改后再定稿。`;
  const pendingEntryTexts = Object.fromEntries(Object.entries(workspace.drawers).map(([key, value]) => [key, value.text]));
  const [savedFeedback, setSavedFeedback] = useState<string | null>(null);
  const [backupFeedback, setBackupFeedback] = useState<string | null>(null);
  const [quoteFeedback, setQuoteFeedback] = useState<string | null>(null);
  const [findingQuote, setFindingQuote] = useState(false);
  const quoteLookupAttemptRef = useRef(0);
  const quoteAccessRef = useRef(canViewCustomerQuote);
  quoteAccessRef.current = canViewCustomerQuote;
  const quoteCreateAccessRef = useRef(canCreateCustomerQuote);
  quoteCreateAccessRef.current = canCreateCustomerQuote;
  const [onlyRisk, setOnlyRisk] = useState(false);
  const [locateLineId, setLocateLineId] = useState<string | null>(null);
  const detailSectionRef = useRef<HTMLDivElement>(null);
  // 行内公式浮层打开即占用编辑位；只有相对当前行公式确有改动才算“未确认修改”。
  const formulaEditorOpen = Boolean(workspace.formula);
  const hasPendingDetailDraft = hasPendingWorkspaceFormula(workspace, runtime.lines);
  const hasInvalidPeopleInput = Object.values(workspace.peopleInputs ?? {}).some((text) => Boolean(quoteBudgetPeopleInputError(text)));
  const [rebuildDialogOpen, setRebuildDialogOpen] = useState(false);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const historyTriggerRef = useRef<HTMLButtonElement>(null);
  const [copyingVersion, setCopyingVersion] = useState(false);
  const [copyingHistoryVersion, setCopyingHistoryVersion] = useState(false);
  const [unfinalizeConfirmOpen, setUnfinalizeConfirmOpen] = useState(false);
  const [regenerateV1ConfirmOpen, setRegenerateV1ConfirmOpen] = useState(false);
  const [finalizeNameOpen, setFinalizeNameOpen] = useState(false);
  const [finalizeName, setFinalizeName] = useState("");
  const [finalizeRiskCodes, setFinalizeRiskCodes] = useState<string[]>([]);
  const hasUnsavedWork = runtime.isDirty || hasPendingEntryText || formulaEditorOpen || hasPendingDetailDraft || hasInvalidPeopleInput;
  const presentedEditorRef = useRef<typeof runtime.editor>(null);
  const presentedDraftRef = useRef(runtime.draft);
  const presentedLinesRef = useRef(runtime.lines);
  const presentedSummaryRef = useRef(runtime.summary);
  const presentedCostBreakdownRef = useRef(runtime.costBreakdown);
  const wasRuntimeDirtyRef = useRef(runtime.isDirty);
  const hasPendingPresentationDraft = hasPendingEntryText || formulaEditorOpen || hasInvalidPeopleInput;
  const runtimePresentationReady = runtime.editor?.revision === runtime.workingRevision;
  if (
    presentedEditorRef.current?.budgetOrder.budgetOrderId !== budgetOrderId
    || (!hasPendingPresentationDraft && runtimePresentationReady)
  ) {
    presentedEditorRef.current = runtime.editor;
    presentedDraftRef.current = runtime.draft;
    presentedLinesRef.current = runtime.lines;
    presentedSummaryRef.current = runtime.summary;
    presentedCostBreakdownRef.current = runtime.costBreakdown;
  } else if (runtime.isDirty || wasRuntimeDirtyRef.current) {
    // 新 revision 的工作态追上前只更新本地改动，避免 editor 与草稿/明细跨 revision 混排。
    presentedDraftRef.current = runtime.draft;
    presentedLinesRef.current = runtime.lines;
    presentedSummaryRef.current = runtime.summary;
    presentedCostBreakdownRef.current = runtime.costBreakdown;
  }
  wasRuntimeDirtyRef.current = runtime.isDirty;
  const presentedEditor = presentedEditorRef.current ?? runtime.editor;
  const presentedDraft = presentedDraftRef.current ?? runtime.draft;
  const presentedLines = presentedLinesRef.current;
  const presentedSummary = presentedSummaryRef.current;
  const presentedCostBreakdown = presentedCostBreakdownRef.current;
  useBusinessPageTitle(presentedEditor
    ? `核价 · ${presentedEditor.itinerarySummary?.title ?? presentedEditor.budgetOrder.budgetOrderNo} · V${presentedEditor.budgetOrder.versionNo}`
    : undefined);
  const unsavedChangesGuard = useUnsavedChangesGuard(hasUnsavedWork);
  // 当前录入场景资源类型，null 时回落到首个资源类型（原型 选择当前录入场景）.
  useEffect(() => () => { quoteLookupAttemptRef.current += 1; }, [budgetOrderId]);

  useEffect(() => {
    setOnlyRisk(false);
    setSavedFeedback(null);
    setBackupFeedback(null);
    setQuoteFeedback(null);
    setRebuildDialogOpen(false);
    setHistoryDrawerOpen(false);
    setUnfinalizeConfirmOpen(false);
    setRegenerateV1ConfirmOpen(false);
    setWorkspace({ activeCategory: "stay", activeDrawerKey: null, drawers: {}, formula: null });
  }, [budgetOrderId]);

  useEffect(() => {
    if (!canOperate) setUnfinalizeConfirmOpen(false);
  }, [canOperate]);

  useEffect(() => {
    if (isCustomerQuoteLockError(runtime.unfinalizeError) || isRosterPricingLockError(runtime.unfinalizeError)) {
      setUnfinalizeConfirmOpen(false);
    }
  }, [runtime.unfinalizeError]);

  const cachePayload = useMemo<QuoteBudgetLocalPayload | null>(() => runtime.draft ? ({ draft: runtime.draft, lines: runtime.lines, workspace }) : null, [runtime.draft, runtime.lines, workspace]);
  const restoreCache = useCallback((payload: QuoteBudgetLocalPayload, revision: string) => {
    if (!runtime.restoreLocalDraft(revision, payload.draft, payload.lines)) return false;
    // 恢复时清掉保存后已失效的修改抽屉，避免「修改范围待确认」一直拦定稿并逼本机缓存反复脏写。
    setWorkspace(pruneStaleReplaceDrawers(payload.workspace, payload.lines));
    return true;
  }, [runtime.restoreLocalDraft]);

  const saveMappingPending = useRef(false);
  useEffect(() => { saveMappingPending.current = false; }, [userId, budgetOrderId, runtime.contextGeneration]);
  useEffect(() => {
    if (!saveMappingPending.current) setWorkspace((previous) => pruneStaleReplaceDrawers(previous, runtime.lines));
  }, [runtime.lines]);
  const hasPendingCachePayload = useCallback((payload: QuoteBudgetLocalPayload) => {
    const people: Record<string, number> = { 学生人数: payload.draft.studentCount, 老师人数: payload.draft.teacherCount,
      导游人数: payload.draft.guideCount, 司机人数: payload.draft.driverCount };
    return hasPendingWorkspaceFormula(payload.workspace, payload.lines)
      || Object.entries(payload.workspace.peopleInputs ?? {}).some(([label, text]) =>
        Boolean(quoteBudgetPeopleInputError(text)) || Number(text) !== people[label])
      || getPendingWorkspaceEntries(payload.workspace, {
        lines: payload.lines, people: payload.draft,
        entryOptions: runtime.editor?.entryOptions ?? { stayAreas: [], points: [] },
        dayCount: inclusiveItineraryDayCount(runtime.editor?.itinerarySummary?.startDate, runtime.editor?.itinerarySummary?.endDate),
        vehicleTypes: legacyBusVehicles.data,
      }).length > 0;
  }, [runtime.editor?.entryOptions, runtime.editor?.itinerarySummary, legacyBusVehicles.data]);
  const localCache = useQuoteBudgetLocalDraft({
    userId, budgetOrderId, baseRevision: runtime.workingRevision, serverRevision: runtime.editor?.revision,
    editable: canOperate && !runtime.readonly, dirty: runtime.isDirty,
    ready: permissionsReady && !runtime.isLoading && !runtime.reloading && !runtime.finalizing && !runtime.unfinalizing && runtimePresentationReady,
    pending: hasPendingEntryText || hasPendingDetailDraft || hasInvalidPeopleInput, payload: cachePayload, onRestore: restoreCache,
    hasPendingPayload: hasPendingCachePayload,
  });
  const reloadAttemptRef = useRef<symbol | null>(null);
  const reloadMountedRef = useRef(true);
  const reloadContextRef = useRef({ userId, budgetOrderId, generation: runtime.contextGeneration });
  reloadContextRef.current = { userId, budgetOrderId, generation: runtime.contextGeneration };
  useEffect(() => {
    reloadMountedRef.current = true;
    return () => { reloadMountedRef.current = false; };
  }, []);
  const [preparingFinalize, setPreparingFinalize] = useState(false);
  const finalizeAttemptRef = useRef<symbol | null>(null);
  const finalizeStateRef = useRef({ budgetOrderId, userId, runtime, localCache, allowed: false });
  finalizeStateRef.current = {
    budgetOrderId, userId, runtime, localCache,
    allowed: canOperate && Boolean(runtime.editor) && !runtime.error && !runtime.readonly
      && !runtime.hasCreatedRebuildVersion && !runtime.saveConflict && !localCache.blocked && !hasUnsavedWork
      && !runtime.saving && !runtime.finalizing && !runtime.unfinalizing
      && !runtime.rebuildingFromItinerary && !runtime.reloadingAfterConflict
      && !copyingVersion && !copyingHistoryVersion
      && ![runtime.saveError, runtime.finalizeError, runtime.unfinalizeError].some(isCustomerQuoteLockError),
  };
  useEffect(() => {
    setPreparingFinalize(false);
    return () => { finalizeAttemptRef.current = null; };
  }, [budgetOrderId, userId, runtime.contextGeneration]);

  /** 打开定稿名称确认框，默认带出当前行程标题。 */
  function openFinalizeName(confirmRiskCodes: string[] = []) {
    setFinalizeRiskCodes(confirmRiskCodes);
    setFinalizeName(runtime.editor?.itinerarySummary?.title ?? runtime.editor?.budgetOrder.versionName ?? "");
    setFinalizeNameOpen(true);
  }

  /** 定稿前同步冗余缓存，等待期间的新输入、权限或版本变化均取消本次提交。 */
  async function handleFinalize(confirmRiskCodes: string[] = [], versionName?: string) {
    const start = finalizeStateRef.current;
    if (finalizeAttemptRef.current || !start.allowed) return;
    const attempt = Symbol("finalize");
    finalizeAttemptRef.current = attempt;
    setPreparingFinalize(true);
    try {
      if (!await start.localCache.prepareFinalize()) return;
      const current = finalizeStateRef.current;
      if (finalizeAttemptRef.current !== attempt || !current.allowed
        || current.budgetOrderId !== start.budgetOrderId || current.userId !== start.userId
        || current.runtime.contextGeneration !== start.runtime.contextGeneration
        || current.runtime.workingRevision !== start.runtime.workingRevision
        || (confirmRiskCodes.length > 0 && !isConfirmableFinalizeRisk(current.runtime.finalizeError))) return;
      current.runtime.finalize(confirmRiskCodes, versionName);
    } finally {
      if (finalizeAttemptRef.current === attempt) {
        finalizeAttemptRef.current = null;
        setPreparingFinalize(false);
      }
    }
  }

  const immediateCacheRef = useRef(false);
  useEffect(() => { if (immediateCacheRef.current) { immediateCacheRef.current = false; void localCache.flush(); } }, [workspace, runtime.lines, localCache.flush]);
  const changeWorkspace = (next: QuoteBudgetWorkspaceState, immediate = false) => {
    immediateCacheRef.current = immediate;
    setWorkspace(next);
  };

  if (runtime.error) {
    return (
      <>
        {unsavedChangesGuard.blocker}
        <div className="quote-budget-editor__load-error" role="alert">
          <span>{editorLoadErrorText(runtime.error)}</span>
          {canViewItinerary ? <Button onClick={() => navigate("/product-lines/course-routes")}>返回行程列表</Button> : <Button onClick={() => navigate("/")}>返回工作台</Button>}
          <Button
            type="primary"
            loading={runtime.reloading}
            onClick={() => void runtime.reload()}
          >
            重新加载
          </Button>
        </div>
      </>
    );
  }
  if (!presentedEditor || !presentedDraft) {
    return <>{unsavedChangesGuard.blocker}<QuoteBudgetLoadingSkeleton /></>;
  }

  const { saving, isDirty } = runtime;
  const draft = presentedDraft;
  const editor = presentedEditor;
  const customerQuoteLockError = [
    runtime.saveError,
    runtime.finalizeError,
    runtime.unfinalizeError,
  ].some(isCustomerQuoteLockError);
  const rosterPricingLocked = editor.readonlyReason === "roster_pricing_locked"
    || [runtime.saveError, runtime.finalizeError, runtime.unfinalizeError].some(isRosterPricingLockError);
  const readonly = editor.readonly
    || rosterPricingLocked
    || customerQuoteLockError
    || runtime.hasCreatedRebuildVersion;
  const authoritativeReadonly = Boolean(runtime.editor?.readonly)
    || rosterPricingLocked
    || customerQuoteLockError
    || runtime.hasCreatedRebuildVersion;
  const authoritativeWriteBlocked = authoritativeReadonly || runtime.saveConflict || localCache.blocked;
  const writeBusy = saving
    || preparingFinalize
    || runtime.finalizing
    || runtime.unfinalizing
    || runtime.regeneratingV1
    || runtime.rebuildingFromItinerary
    || runtime.reloadingAfterConflict
    || copyingVersion
    || copyingHistoryVersion;
  const order = editor.budgetOrder;
  const auditLineIds = new Set(presentedCostBreakdown?.auditIssues.flatMap((issue) => issue.budgetLineId ? [issue.budgetLineId] : []) ?? []);
  const riskLines = presentedLines.filter((line) => !isBusinessTaxLine(line) && (
    line.priceSourceType === "unpriced" || isMissingPointUnitPrice(line) || auditLineIds.has(line.budgetLineId)
    || (["住宿", "考察点", "包车", "大交通"].includes(line.resourceType) && Number(line.budgetAmount) === 0)
  ));
  const riskReasonByLineId = Object.fromEntries(riskLines.map((line) => {
    const reasons = [
      line.priceSourceType === "unpriced" || isMissingPointUnitPrice(line) ? "待补价" : "",
      auditLineIds.has(line.budgetLineId) ? "审计待核验" : "",
      ["住宿", "大交通", "包车", "考察点"].includes(line.resourceType) && Number(line.budgetAmount) === 0 ? "关键费用为 0 元" : "",
    ].filter(Boolean);
    return [line.budgetLineId, reasons.join("；")];
  }));
  const unpricedLineCount = presentedLines.filter((line) => line.priceSourceType === "unpriced" || isMissingPointUnitPrice(line)).length;
  const customerQuoteLocked = editor.readonlyReason === "customer_quote_finalized"
    || Boolean(order.lockedByCustomerQuoteId)
    || customerQuoteLockError;
  const showRegenerateV1 = order.versionNo === 1
    && editor.readonlyReason === "initial_version"
    && canOperate
    && !customerQuoteLocked;
  const showFinalize = !authoritativeReadonly;
  const showUnfinalize = readonly && order.status === "finalized" && !customerQuoteLocked && !rosterPricingLocked;
  const saveErrorText = (() => {
    if (!runtime.saveError || runtime.saveConflict || isCustomerQuoteLockError(runtime.saveError)) return null;
    if (isRosterPricingLockError(runtime.saveError)) return readonlyReasonText("roster_pricing_locked");
    const error = normalizeHttpError(runtime.saveError);
    if (error.statusCode === 401) return "登录状态已失效，请重新登录后保存。";
    if (error.statusCode === 403) return "当前账号没有修改此核价的权限；本地内容仍保留，请复制或下载草稿后联系管理员。";
    if (error.statusCode === 422) return resolveReadableHttpErrorMessage(error,
      "核价内容未通过服务端校验，请检查价格、数量和明细后重试。",
      { validationFallback: "核价内容未通过服务端校验，请检查价格、数量和明细后重试。" });
    if (error.statusCode === 408 || error.statusCode === 504 || error.code === "ECONNABORTED") return "保存请求超时，当前内容仍保留，请稍后重试或先复制本地草稿。";
    if (error.code === "ERR_NETWORK" || error.code === "NETWORK_ERROR") return "无法连接核价服务，当前内容仍保留，请检查网络后重试。";
    return "保存草稿失败，请稍后重试。";
  })();
  const saveErrorLineNo = saveErrorText?.match(/第(\d+)行/u)?.[1];
  const saveErrorLine = saveErrorLineNo ? presentedLines.find(line => line.lineNo === Number(saveErrorLineNo)) : undefined;
  const finalizeErrorText = finalizeBlockText(runtime.finalizeError);
  const canConfirmFinalizeRisk = isConfirmableFinalizeRisk(runtime.finalizeError);
  const zeroKeyLineRiskSummary = canConfirmFinalizeRisk
    ? formatZeroKeyLineRiskSummary(presentedLines)
    : null;
  const finalizeFeedbackText = finalizeErrorText && zeroKeyLineRiskSummary
    ? `${finalizeErrorText}（0 元明细：${zeroKeyLineRiskSummary}）`
    : finalizeErrorText;
  const itineraryChanged = isItineraryChangedFinalizeError(runtime.finalizeError);
  const rebuildBlockedReason = runtime.saveConflict || localCache.blocked
    ? "核价已被其他用户更新，请先重新加载最新数据后再生成新版本。"
    : hasInvalidPeopleInput
      ? "人数输入不合法，请先修正后再生成新版本。"
    : hasPendingEntryText
      ? "快速录入区有未加入内容，请先加入明细或清空后再生成新版本。"
    : formulaEditorOpen
      ? "明细修改尚未应用，请先应用或取消后再生成新版本。"
    : isDirty
      ? "草稿已发生变化，请先关闭弹窗并保存草稿。"
      : undefined;

  /** 按当前行程重建预算版本并在数据预载后切换路由。 */
  async function handleRebuildFromItinerary(reuseExactMatchedPrices: boolean) {
    if (rebuildBlockedReason) return;
    try {
      const result = await runtime.rebuildFromItinerary(reuseExactMatchedPrices);
      navigate(`/quote/budgets/${result.budgetOrderId}`);
    } catch {
      // mutation 错误由页面反馈区统一展示，路由保持当前预算版本。
    }
  }
  /** 打开重建弹窗并异步加载可复用价格预览。 */
  function openRebuildDialog() {
    if (rebuildBlockedReason) return;
    setRebuildDialogOpen(true);
    void runtime.loadPriceReusePreview().catch(() => undefined);
  }
  /** 行内公式确认只写当前工作态，数据库写入由保存草稿统一处理。 */
  function handleApplyFormula(lineId: string, formulaText: string) {
    if (authoritativeWriteBlocked || writeBusy) return false;
    if (!runtime.applyLineFormula(lineId, formulaText)) return false;
    return true;
  }

  /** 先读取权威数据，再按点击时的身份和原文处理冲突，失败保持整个原现场。 */
  async function handleReloadAfterConflict() {
    if (reloadAttemptRef.current || writeBusy) return;
    if (
      (hasUnsavedWork || localCache.blocked)
      && !window.confirm("重新加载最新数据将丢弃当前未保存内容，是否继续？")
    ) return;
    const context = reloadContextRef.current;
    const token = Symbol("reload-budget");
    reloadAttemptRef.current = token;
    const current = () => reloadMountedRef.current
      && reloadContextRef.current.userId === context.userId
      && reloadContextRef.current.budgetOrderId === context.budgetOrderId
      && reloadContextRef.current.generation === context.generation;
    const attempt = localCache.prepareDiscard();
    try {
      await runtime.reloadLatestAfterConflict(apply => attempt.commit(() => {
        if (!current() || !apply()) return false;
        setWorkspace({ activeCategory: "stay", activeDrawerKey: null, drawers: {}, formula: null });
        return true;
      }, current));
    } catch {
      // 两类冲突共用下方可见错误；失败不清理工作区。
    } finally {
      attempt.cancel();
      if (reloadAttemptRef.current === token) reloadAttemptRef.current = null;
    }
  }
  /** 只读定位已有报价，确认不存在且有创建权限时才进入创建链。 */
  async function handleOpenCustomerQuote() {
    if (!canViewCustomerQuote || findingQuote) return;
    if (order.lockedByCustomerQuoteId) {
      navigate(`/quote/customer-quotes/${order.lockedByCustomerQuoteId}`);
      return;
    }
    setFindingQuote(true);
    setQuoteFeedback(null);
    const attempt = ++quoteLookupAttemptRef.current;
    const current = () => attempt === quoteLookupAttemptRef.current && quoteAccessRef.current;
    try {
      const quoteId = await findExistingCustomerQuote(budgetOrderId);
      if (!current()) return;
      if (!quoteId?.trim()) throw new Error("报价标识缺失");
      navigate(`/quote/customer-quotes/${quoteId}`);
    } catch (error) {
      if (!current()) return;
      const normalized = normalizeHttpError(error);
      if (normalized.statusCode === 404 && normalized.code === "CUSTOMER_QUOTE_NOT_FOUND") {
        if (quoteCreateAccessRef.current) navigate(`/quote/budgets/${budgetOrderId}/customer-quote`);
        else setQuoteFeedback("此核价尚无行程报价，请联系有创建权限的同事生成报价。");
      } else {
        setQuoteFeedback(normalized.statusCode === 403 ? "当前账号没有查看行程报价的权限。" : "查询已有报价失败，请重试；当前未创建报价。");
      }
    } finally { if (current()) setFindingQuote(false); }
  }
  /** 导出当前工作态供人工恢复，包含尚未加入的各类别文本。 */
  function localDraftBackup() {
    return JSON.stringify({
      说明: "本地核价草稿，仅供人工核对恢复，未写入服务器", 导出时间: new Date().toISOString(),
      budgetOrderId, revision: runtime.workingRevision, budgetOrder: order,
      peopleCounts: runtime.draft, lines: runtime.lines, pendingEntryTexts, workspace,
      localCache: localCache.exportBackup(),
    }, null, 2);
  }
  /** 主动复制本地快照；剪贴板受限时保留下载入口。 */
  async function copyLocalDraft() {
    try {
      await navigator.clipboard.writeText(localDraftBackup());
      setBackupFeedback("已复制本地草稿，请粘贴到安全位置保存后再重新加载。");
    } catch { setBackupFeedback("复制失败，请使用下载本地草稿保留当前内容。"); }
  }
  /** 下载当前快照供人工恢复，不调用任何业务写接口。 */
  function downloadLocalDraft() {
    const url = URL.createObjectURL(new Blob([localDraftBackup()], { type: "application/json;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `核价草稿-${order.budgetOrderNo}-V${order.versionNo}.json`;
    link.click();
    URL.revokeObjectURL(url);
    setBackupFeedback("已生成本地草稿下载，请确认文件已保存后再重新加载。");
  }
  /** 切到目标费用分类并滚到首条待补价；不自动勾选「只看待处理明细」。 */
  function locateRiskLines() {
    const target = selectLocateRiskLine(riskLines);
    if (!target) return;
    setOnlyRisk(false);
    const nextCategory = workspaceCategory(target);
    setWorkspace((previous) => ({
      ...previous,
      activeCategory: nextCategory,
      activeDrawerKey: null,
      formula: null,
    }));
    setLocateLineId(target.budgetLineId);
  }
  return (
    <>
      {unsavedChangesGuard.blocker}
      <div className="quote-budget-editor">
      <header className="quote-budget-editor__header">
        {runtime.refreshError ? (
          <div
            className="quote-budget-editor__feedback quote-budget-editor__feedback--error quote-budget-editor__refresh-error"
            role="alert"
          >
            <span>行程核价刷新失败，请稍后重试。</span>
            <Button
              size="mini"
              loading={runtime.reloading}
              onClick={() => void runtime.reload()}
            >
              重试刷新
            </Button>
          </div>
        ) : null}
        {customerQuoteLocked ? (
          <Alert
            className="quote-budget-editor__customer-quote-lock"
            type="warning"
            title="报价已定稿，当前核价版本不能修改"
            content={(
              <span>
                如需调整，请复制核价版本后重新核价。
                {runtime.lockRefreshError ? (
                  <strong>
                    锁定数据刷新失败：{runtime.lockRefreshError.message}，请刷新页面后重试。
                  </strong>
                ) : null}
                <time dateTime={order.customerQuoteLockedAt ?? undefined}>
                  锁定时间：{formatCustomerQuoteLockedAt(order.customerQuoteLockedAt)}
                </time>
                {order.lockedByCustomerQuoteId && canViewCustomerQuote ? (
                  <Link to={`/quote/customer-quotes/${order.lockedByCustomerQuoteId}`}>
                    查看已定稿对客报价
                  </Link>
                ) : null}
              </span>
            )}
            showIcon
          />
        ) : null}
        {runtime.saveConflict ? (
          <div
            className="quote-budget-editor__feedback quote-budget-editor__feedback--error quote-budget-editor__save-conflict"
            role="alert"
          >
            <span>核价已被其他用户更新，当前未保存内容已保留。</span>
            <Button size="mini" onClick={() => void copyLocalDraft()}>复制本地草稿</Button>
            <Button size="mini" onClick={downloadLocalDraft}>下载本地草稿</Button>
            {backupFeedback ? <span role="status">{backupFeedback}</span> : null}
            <Button
              size="mini"
              loading={runtime.reloadingAfterConflict}
              onClick={() => void handleReloadAfterConflict()}
            >
              重新加载最新数据
            </Button>
          </div>
        ) : null}
        <h1>行程核价</h1>
        <p className="quote-budget-editor__eyebrow">{order.budgetOrderNo}</p>
        <span className="quote-budget-editor__version-meta">
          <span>{order.versionName} · 版本 V{order.versionNo}</span>
          <span className="quote-budget-status-pill">
            {versionStatusText(order.status)}
          </span>
          {!localCache.blocked && localCache.message ? (
            <span className="quote-budget-editor__header-status" role="status">{localCache.message}</span>
          ) : null}
          {isDirty ? <span className="quote-budget-status-pill quote-budget-status-pill--dirty">明细未保存</span> : null}
          {hasPendingDetailDraft ? <span className="quote-budget-status-pill quote-budget-status-pill--dirty">明细修改未确认</span> : null}
          {hasPendingEntryText ? <span className="quote-budget-status-pill quote-budget-status-pill--dirty">录入内容尚未加入明细</span> : null}
          {(showFinalize && canOperate && (hasPendingDetailDraft || hasPendingEntryText || isDirty))
            || (itineraryChanged && canOperate && isDirty) ? (
            <span className="quote-budget-editor__header-tips">
              {showFinalize && canOperate && (hasPendingDetailDraft || hasPendingEntryText || isDirty) ? (
                <span className="quote-budget-editor__workflow-note" role="note">
                  {hasPendingDetailDraft
                    ? "请先确定或取消明细修改，再保存草稿后定稿"
                    : hasPendingEntryText
                      ? pendingEntryFinalizeText
                      : "请先保存草稿后再定稿"}
                </span>
              ) : null}
              {pendingEntries.map(entry => <Button key={entry.key} type="text" size="mini"
                aria-label={`处理未确认录入：${entry.label}`} disabled={writeBusy || formulaEditorOpen}
                onClick={() => changeWorkspace({ ...workspace, activeCategory: entry.draft.category, activeDrawerKey: entry.key })}>
                处理：{entry.label}
              </Button>)}
              {itineraryChanged && canOperate && isDirty ? (
                <span className="quote-budget-editor__workflow-note" role="note">
                  请先保存草稿后再生成新版本
                </span>
              ) : null}
            </span>
          ) : null}
          {editor.historySource ? (
            <span className="quote-budget-history-source">
              来源：{editor.historySource.itineraryTitle} · {editor.historySource.budgetOrderNo}
              {" · "}V{editor.historySource.versionNo}
            </span>
          ) : null}
        </span>
        {rosterPricingLocked ? <span role="note">{readonlyReasonText("roster_pricing_locked")}</span> : readonly && editor.readonlyReason && !customerQuoteLocked ? (
          <span role="note">{readonlyReasonText(editor.readonlyReason)}</span>
        ) : null}
        {!canOperate ? <span role="note">当前账号仅可查看核价。{order.status === "draft" ? "核价尚未重新定稿，当前展示计调已保存的草稿。" : ""}</span> : null}
        <div className="quote-budget-editor__actions">
          {returnTo ? <Link to={returnTo}>返回原报价</Link> : null}
          {canOperate ? (
            <Button
              ref={historyTriggerRef}
              icon={<HistoryIcon size={16} />}
              disabled={writeBusy}
              onClick={() => setHistoryDrawerOpen(true)}
            >
              获取历史数据
            </Button>
          ) : null}
          <Button
            icon={<GitCompare size={16} />}
            disabled={writeBusy}
            onClick={() => window.open(
              `/quote/budgets/${encodeURIComponent(budgetOrderId)}/compare`,
              "_blank",
              "noopener,noreferrer",
            )}
          >
            版本对比
          </Button>
          {showRegenerateV1 ? (
            <Button
              status="warning"
              loading={runtime.regeneratingV1}
              disabled={writeBusy || runtime.saveConflict || localCache.blocked}
              onClick={() => setRegenerateV1ConfirmOpen(true)}
            >
              按最新行程重新生成 V1
            </Button>
          ) : null}
          {/* 复制对历史/定稿版本同样可用，故不随 readonly 禁用. */}
          {!rosterPricingLocked ? <QuoteVersionActions
            sourceBudgetOrderId={budgetOrderId}
            contextGeneration={runtime.contextGeneration}
            isDirty={hasUnsavedWork}
            hasPendingEntryText={hasPendingEntryText}
            disabled={writeBusy || runtime.saveConflict}
            canCreate={canOperate}
            onCopyingChange={setCopyingVersion}
            onNavigateVersion={(targetBudgetOrderId) => unsavedChangesGuard.runWithNavigationBypass(
              () => navigate(`/quote/budgets/${targetBudgetOrderId}`),
            )}
          /> : null}
          {canImportConfirmation ? <QuoteBudgetImportButton key={budgetOrderId} budgetId={budgetOrderId} itineraryId={order.routeId} revision={runtime.editor?.revision ?? ""} blocked={hasUnsavedWork || writeBusy || localCache.blocked || runtime.saveConflict || !runtimePresentationReady} onImported={workspaceId => navigate(`/supply-chain/fulfillment-orders/workspaces/${workspaceId}`)} /> : null}
          {canOperate ? (
            <Button
              type="primary"
              title={formulaEditorOpen
                ? "请先确定或取消明细修改，再保存草稿"
                : hasPendingEntryText
                  ? isDirty
                    ? `保存草稿只保存已加入的明细；未确认录入：${pendingEntryLabels}`
                    : `明细已保存；未确认录入：${pendingEntryLabels}`
                  : undefined}
              disabled={authoritativeWriteBlocked || !isDirty || writeBusy || formulaEditorOpen || hasInvalidPeopleInput}
              loading={saving}
              onClick={() => {
                setSavedFeedback(null);
                const saveToken = localCache.beginSave();
                const submitted = runtime.lines;
                const saveContext = reloadContextRef.current;
                saveMappingPending.current = true;
                void runtime.save().then((saved) => {
                  const current = reloadContextRef.current;
                  if (!reloadMountedRef.current || current.userId !== saveContext.userId
                    || current.budgetOrderId !== saveContext.budgetOrderId || current.generation !== saveContext.generation) return;
                  setWorkspace(previous => remapSavedWorkspace(previous, submitted, saved.lines));
                  saveMappingPending.current = false;
                  localCache.afterSave(saved.revision, saveToken);
                  setSavedFeedback(`已于 ${nowLabel()} 保存已加入的明细`);
                }).catch(() => { saveMappingPending.current = false; localCache.saveFailed(saveToken); });
              }}
            >
              保存草稿
            </Button>
          ) : null}
          {order.status === "finalized" && canViewCustomerQuote ? (
            <Button
              type="primary"
              icon={<ReceiptText size={16} />}
              disabled={runtime.unfinalizing}
              loading={findingQuote}
              onClick={() => void handleOpenCustomerQuote()}
            >
              {order.lockedByCustomerQuoteId || !canCreateCustomerQuoteFromBudget ? "查看已有报价" : "行程报价"}
            </Button>
          ) : null}
          {quoteFeedback ? <span role="alert">{quoteFeedback}</span> : null}
          {localCache.blocked && localCache.message ? <span role="alert">{localCache.message}</span> : null}
          {hasInvalidPeopleInput ? <span role="alert">请先修正左侧人数输入，再保存或定稿。</span> : null}
          {localCache.blocked ? <><Button onClick={() => void copyLocalDraft()}>复制本地草稿</Button><Button onClick={downloadLocalDraft}>下载本地草稿</Button><Button loading={runtime.reloadingAfterConflict} disabled={writeBusy} onClick={() => void handleReloadAfterConflict()}>放弃本地并重新加载</Button></> : null}
          {runtime.conflictReloadError ? <span role="alert">重新加载最新数据失败，请稍后重试。</span> : null}
          {savedFeedback ? <span role="status">{savedFeedback}{isDirty ? "；之后还有未保存修改" : ""}</span> : null}
          {showFinalize && canOperate ? (
            <Button
              status="success"
              title={formulaEditorOpen
                ? "请先确定或取消明细修改，再保存草稿后定稿"
                : hasPendingEntryText
                  ? pendingEntryFinalizeText
                  : isDirty
                    ? "请先保存草稿后再定稿"
                    : undefined}
              loading={runtime.finalizing || preparingFinalize}
              disabled={hasUnsavedWork || writeBusy || runtime.saveConflict || localCache.blocked}
              onClick={() => openFinalizeName()}
            >
              定稿
            </Button>
          ) : null}
          {saveErrorText ? (
            <div className="quote-budget-editor__feedback quote-budget-editor__feedback--error" role="alert">
              {saveErrorText}
              {saveErrorLine ? <Button size="small" onClick={() => {
                setOnlyRisk(false);
                setWorkspace(previous => ({ ...previous, activeCategory: workspaceCategory(saveErrorLine), activeDrawerKey: null }));
                setLocateLineId(saveErrorLine.budgetLineId);
              }}>定位明细</Button> : null}
              <Button size="mini" onClick={() => void copyLocalDraft()}>复制本地草稿</Button>
              <Button size="mini" onClick={downloadLocalDraft}>下载本地草稿</Button>
              {backupFeedback ? <span role="status">{backupFeedback}</span> : null}
            </div>
          ) : null}
          {showFinalize && canOperate && canConfirmFinalizeRisk ? (
            <Button
              status="warning"
              disabled={hasUnsavedWork || writeBusy || runtime.saveConflict || localCache.blocked}
              onClick={() => openFinalizeName([...CONFIRMABLE_FINALIZE_RISK_CODES])}
            >
              确认风险并定稿
            </Button>
          ) : null}
          {showUnfinalize && canOperate ? (
            <Button
              status="warning"
              loading={runtime.unfinalizing}
              disabled={writeBusy}
              onClick={() => setUnfinalizeConfirmOpen(true)}
            >
              取消定稿
            </Button>
          ) : null}
          {finalizeFeedbackText ? (
            <div className="quote-budget-editor__feedback quote-budget-editor__feedback--error" role="alert">
              {finalizeFeedbackText}
              {riskLines.length > 0 ? <Button size="mini" disabled={formulaEditorOpen} onClick={locateRiskLines}>定位问题明细</Button> : null}
            </div>
          ) : null}
          {itineraryChanged && canOperate ? (
            <Button
              type="primary"
              loading={runtime.rebuildingFromItinerary}
              disabled={Boolean(rebuildBlockedReason) || writeBusy}
              onClick={openRebuildDialog}
            >
              基于最新行程生成新版本
            </Button>
          ) : null}
        </div>

      </header>
      <div className="quote-budget-editor__workspace">
        <aside
          className="quote-budget-editor__rail"
          aria-label="核价左侧信息栏"
          data-scroll-container="left"
        >
          <QuoteTripCard summary={editor.itinerarySummary} budgetStatus={order.status} canView={canViewItinerary} canEdit={canEditItinerary} />
          <QuotePeopleCountPanel
            key={`${budgetOrderId}:${editor.revision}`}
            value={draft}
            inputs={workspace.peopleInputs}
            onInputChange={(label, text) => setWorkspace((previous) => ({ ...previous, peopleInputs: { ...previous.peopleInputs, [label]: text } }))}
            disabled={authoritativeWriteBlocked || !canOperate || writeBusy}
            onStudentCountChange={runtime.setStudentCount}
            onTeacherCountChange={runtime.setTeacherCount}
            onGuideCountChange={runtime.setGuideCount}
            onDriverCountChange={runtime.setDriverCount}
            onTeacherPaysChange={runtime.setTeacherPays}
          />
          <QuoteVersionPanel
            disabled={writeBusy}
            versions={editor.versions}
            currentBudgetOrderId={budgetOrderId}
            contextGeneration={runtime.contextGeneration}
            summary={presentedSummary}
            costBreakdown={presentedCostBreakdown}
            auditPending={isDirty}
            unpricedLineCount={unpricedLineCount}
            onLocateRisk={formulaEditorOpen ? undefined : locateRiskLines}
            teacherPays={draft.teacherPays}
            isDirty={hasUnsavedWork}
            hasPendingEntryText={hasPendingEntryText}
            onLoadVersion={runtime.preloadVersion}
            onNavigateVersion={(targetBudgetOrderId) => unsavedChangesGuard.runWithNavigationBypass(
              () => navigate(`/quote/budgets/${targetBudgetOrderId}`),
            )}
          />
        </aside>
        <section
          className="quote-budget-editor__center"
          aria-label="核价右侧编辑区"
          data-scroll-container="right"
        >
          <div ref={detailSectionRef} tabIndex={-1} className="quote-budget-detail-anchor" />
          <QuoteBudgetManualWorkspace
            lines={presentedLines} people={draft} teacherPays={draft.teacherPays} entryOptions={editor.entryOptions} versionNo={order.versionNo}
            dayCount={inclusiveItineraryDayCount(editor.itinerarySummary?.startDate, editor.itinerarySummary?.endDate)}
            readonly={authoritativeWriteBlocked || !canOperate} busy={writeBusy} onlyRisk={onlyRisk}
            locateLineId={locateLineId} onLocateLineHandled={() => setLocateLineId(null)}
            riskReasonByLineId={riskReasonByLineId} state={workspace} onChange={changeWorkspace}
            toolbarControls={<>
              {riskLines.length > 0 || onlyRisk ? <Checkbox checked={onlyRisk} disabled={formulaEditorOpen} onChange={setOnlyRisk}>只看待处理明细（{riskLines.length}）</Checkbox> : null}
              {(runtime.deletedLineCountByCategory[workspace.activeCategory] ?? 0) > 0 ? <Button size="mini" disabled={authoritativeWriteBlocked || writeBusy || !canOperate} onClick={() => { runtime.undoLastDeletion(workspace.activeCategory); immediateCacheRef.current = true; }}>撤销上次删除（{runtime.deletedLineCountByCategory[workspace.activeCategory]} 条）</Button> : null}
            </>}
            onCommit={(next, ids) => { if (authoritativeWriteBlocked || writeBusy) return false; const result = runtime.commitManualLines(next, ids); immediateCacheRef.current = result; return result; }}
            onRemove={(ids) => { if (authoritativeWriteBlocked || writeBusy) return; runtime.removeLines(ids); immediateCacheRef.current = true; }}
            onApplyLegacyFormula={handleApplyFormula}
          />
        </section>
      </div>
      {finalizeNameOpen ? <Modal
        title="确认定稿名称"
        visible
        okText="确认定稿"
        cancelText="取消"
        okButtonProps={{ disabled: !finalizeName.trim() }}
        onOk={() => {
          const name = finalizeName.trim();
          if (!name) return;
          setFinalizeNameOpen(false);
          void handleFinalize(finalizeRiskCodes, name);
        }}
        onCancel={() => setFinalizeNameOpen(false)}
      >
        <Input aria-label="定稿名称" value={finalizeName} maxLength={300} onChange={setFinalizeName} />
      </Modal> : null}
      <QuoteRebuildFromItineraryDialog
        visible={rebuildDialogOpen}
        preview={runtime.priceReusePreview}
        loadingPreview={runtime.loadingPriceReusePreview}
        previewError={runtime.priceReusePreviewError}
        creating={runtime.rebuildingFromItinerary}
        createError={runtime.rebuildFromItineraryError}
        hasCreatedVersion={runtime.hasCreatedRebuildVersion}
        hasStartedRequest={runtime.hasStartedRebuildRequest}
        fixedReuseExactMatchedPrices={runtime.rebuildReuseExactMatchedPrices}
        blockedReason={rebuildBlockedReason}
        onRetryPreview={async () => {
          await runtime.loadPriceReusePreview().catch(() => undefined);
        }}
        onConfirm={handleRebuildFromItinerary}
        onCancel={() => {
          if (!runtime.rebuildingFromItinerary) setRebuildDialogOpen(false);
        }}
      />
      <Modal
        visible={unfinalizeConfirmOpen && showUnfinalize && canOperate}
        title="确认取消定稿？"
        okText="确认取消"
        cancelText="保留定稿"
        confirmLoading={runtime.unfinalizing}
        okButtonProps={{ disabled: !canOperate || writeBusy }}
        maskClosable={!runtime.unfinalizing}
        closable={!runtime.unfinalizing}
        onCancel={() => {
          if (!runtime.unfinalizing) setUnfinalizeConfirmOpen(false);
        }}
        onOk={() => void runtime.unfinalize()
          .then(() => setUnfinalizeConfirmOpen(false))
          .catch(() => undefined)}
      >
        <p>取消后该版本将恢复为草稿并允许编辑。</p>
        {runtime.unfinalizeError ? (
          <Alert type="error" content={isRosterPricingLockError(runtime.unfinalizeError) ? readonlyReasonText("roster_pricing_locked") : "取消定稿失败，请稍后重试。"} showIcon />
        ) : null}
      </Modal>
      <Modal
        visible={regenerateV1ConfirmOpen && showRegenerateV1}
        title="按最新行程重新生成 V1？"
        okText="重新生成"
        cancelText="取消"
        confirmLoading={runtime.regeneratingV1}
        okButtonProps={{ status: "warning", disabled: writeBusy || runtime.saveConflict || localCache.blocked }}
        maskClosable={!runtime.regeneratingV1}
        closable={!runtime.regeneratingV1}
        onCancel={() => {
          if (!runtime.regeneratingV1) setRegenerateV1ConfirmOpen(false);
        }}
        onOk={() => void runtime.regenerateV1()
          .then(() => setRegenerateV1ConfirmOpen(false))
          .catch(() => undefined)}
      >
        <p>将用当前行程结构覆盖本条 V1 明细与人数，版本号仍为 V1；已有 V2 及以后版本不会自动变更。</p>
        {runtime.regenerateV1Error ? (
          <Alert type="error" content="重新生成 V1 失败，请稍后重试。" showIcon />
        ) : null}
      </Modal>
      <QuoteHistoryDrawer
        visible={historyDrawerOpen}
        targetBudgetOrderId={budgetOrderId}
        isDirty={hasUnsavedWork}
        hasPendingEntryText={hasPendingEntryText}
        hasPendingDetailDraft={formulaEditorOpen}
        hasSaveConflict={runtime.saveConflict}
        onCancel={() => {
          if (!copyingHistoryVersion) setHistoryDrawerOpen(false);
        }}
        onAfterClose={() => historyTriggerRef.current?.focus()}
        onCopyingChange={setCopyingHistoryVersion}
        onLoadVersion={runtime.preloadVersion}
        onNavigateVersion={(targetBudgetOrderId) => unsavedChangesGuard.runWithNavigationBypass(
          () => navigate(`/quote/budgets/${targetBudgetOrderId}`),
        )}
      />
      </div>
    </>
  );
}
